import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';

const SEOHead = ({ 
  title, 
  description, 
  keywords, 
  canonical,
  ogImage,
  ogType = 'website'
}) => {
  const { currentLanguage, getPathWithLanguage } = useLanguage();
  const { t } = useTranslation();

  const baseUrl = window.location.origin;
  const currentPath = window.location.pathname;
  const pathWithoutLang = currentPath.replace(/^\/(en|fr|es|it|ru|ar|de)/, '') || '/';

  // Generate hreflang URLs
  const hreflangs = [
    { lang: 'en', url: `${baseUrl}${getPathWithLanguage(pathWithoutLang, 'en')}` },
    { lang: 'fr', url: `${baseUrl}${getPathWithLanguage(pathWithoutLang, 'fr')}` },
    { lang: 'es', url: `${baseUrl}${getPathWithLanguage(pathWithoutLang, 'es')}` },
    { lang: 'it', url: `${baseUrl}${getPathWithLanguage(pathWithoutLang, 'it')}` },
    { lang: 'ru', url: `${baseUrl}${getPathWithLanguage(pathWithoutLang, 'ru')}` },
    { lang: 'ar', url: `${baseUrl}${getPathWithLanguage(pathWithoutLang, 'ar')}` },
    { lang: 'de', url: `${baseUrl}${getPathWithLanguage(pathWithoutLang, 'de')}` },
    { lang: 'x-default', url: `${baseUrl}${getPathWithLanguage(pathWithoutLang, 'en')}` }
  ];

  const canonicalUrl = canonical || `${baseUrl}${currentPath}`;
  const finalTitle = title || t('seo.homeTitle');
  const finalDescription = description || t('seo.homeDescription');
  const finalKeywords = keywords || t('seo.homeKeywords');

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={currentLanguage} />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="robots" content="index, follow" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang Tags */}
      {hreflangs.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={
        currentLanguage === 'en' ? 'en_US' :
        currentLanguage === 'fr' ? 'fr_FR' :
        currentLanguage === 'es' ? 'es_ES' :
        currentLanguage === 'it' ? 'it_IT' :
        currentLanguage === 'ru' ? 'ru_RU' :
        currentLanguage === 'ar' ? 'ar_AR' :
        currentLanguage === 'de' ? 'de_DE' : 'en_US'
      } />
      {currentLanguage !== 'en' && <meta property="og:locale:alternate" content="en_US" />}
      {currentLanguage !== 'fr' && <meta property="og:locale:alternate" content="fr_FR" />}
      {currentLanguage !== 'es' && <meta property="og:locale:alternate" content="es_ES" />}
      {currentLanguage !== 'it' && <meta property="og:locale:alternate" content="it_IT" />}
      {currentLanguage !== 'ru' && <meta property="og:locale:alternate" content="ru_RU" />}
      {currentLanguage !== 'ar' && <meta property="og:locale:alternate" content="ar_AR" />}
      {currentLanguage !== 'de' && <meta property="og:locale:alternate" content="de_DE" />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      
      {/* Language and Region */}
      <meta name="language" content={currentLanguage} />
      <meta name="geo.region" content="BA" />
      <meta name="geo.country" content="Bosnia and Herzegovina" />
    </Helmet>
  );
};

export default SEOHead;
